import * as React from "react";
import { getCategoryDisplayLabel, resolvePharmacyKey } from "../../../utils/Utility";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  styled,
  Switch,
  switchClasses,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Button from "../../common/Button";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../../theme";
import {
  AddCircleOutlineOutlined,
  DeleteOutlineOutlined,
  Save,
  ManageSearch,
  BusinessOutlined,
  HealthAndSafetyOutlined,
  VerifiedUserOutlined,
  SyncAltOutlined,
  LocalShippingOutlined,
  AssignmentOutlined,
  AccountBalanceWalletOutlined,
  MedicalServicesOutlined,
  ModeOutlined,
} from "@mui/icons-material";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import CustomTextField from "../../common/CustomTextField";
import { Controller, useForm } from "react-hook-form";
import CustomNametField from "../../common/CustomNametField";
import CustomPatternTextField from "../../common/CustomPatternTextField";
// Added useRef
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useCodetablesSlice,
  getCodeTableValuesByTableName,
  CodeTable,
} from "../../../redux/slice/codetableSlice";
import {
  editPracticeData,
  usePracticeEditSlice,
  fetchPracticeByIdData,
  fetchAuditLogsForPracticeDOSPreference,
  savePracticePharmacyConfigData,
} from "../../../redux/slice/practice/practiceEditSlice";
import { PracticeInfo } from "../../../redux/slice/practice/practiceRegistrationSlice";
import {
  usePracticeProductsSlice
} from "../../../redux/slice/practice/practiceProductSlice";
import CustomAlert from "../../common/CustomAlert";
import {
  SingleAlertInfo,
  setSingleAlertObj,
  fetchPharmaciesList,
  useCommonReducer,
} from "../../../redux/slice/commonSlice";
import OptionsPopup from "../../common/OptionsPopup";
import CustomRadioButton from "../../common/CustomRadioButton";
import { useNavigation } from "../../../hooks/useNavigation";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import { useAuthSlice } from "../../../redux/slice/authSlice";
import { PHARMACY_CONFIG_BILLING_CODE, PRACTICE_TYPE, PRODUCT_CATEGORY, PRODUCT_CATEGORY_CODETABLE, USER_ROLE_ENTITY_NAMES, USER_ROLES } from "../../../constants/applicationConstants";
import CustomCheckboxGroup from "../../common/CustomCheckboxGroup";
import CustomNumberField from "../../common/CustomNumberField";
import DOSConfirmationPopup from "./DOSConfirmationPopup";
import AuditLogDisplayPopup, { AuditLogData } from "./AuditLogDisplayPopup";
import { SectionHeader } from "../../ui/ContentSection";
import CustomIconButton from "../../common/CustomIconButton";
import Table from "../../common/CustomTable";
import PracticePharmacyPopup, { PracticePharmacyRow } from "./PracticePharmacyPopup";
import { useModalStyles } from "../../common/useModalStyles";

interface ContactFormProps {
  activeStep: number;
  contentChangedStatus: () => void;
}

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "center" | "left";
  format?: (value: number) => string;
}

/**
 * CS-2075 — key a pharmacy's billing code is stored under in practice_pharmacy_config.
 * Same lowercase-contains match as backend resolvePharmacyKey (genesis, then sandsrx).
 */
const toPharmacyConfigKey = (
  manufacturerName: string | null | undefined,
  isPharmacy?: boolean | null
): string => resolvePharmacyKey(manufacturerName, isPharmacy) || "";

const PracticeEditViewForm: React.FC<ContactFormProps> = ({ activeStep, contentChangedStatus }) => {
  const columns3: Column[] = [
    { id: "productName", label: "Product Name", minWidth: 100 },
    { id: "agreementStatus", label: "Agreement Status", minWidth: 100 },
  ];

  // Section refs — one per visual section in DOM order
  const practiceInfoRef = useRef<HTMLDivElement>(null);
  const specializationRef = useRef<HTMLDivElement>(null);
  const emrIntegrationRef = useRef<HTMLDivElement>(null);
  const dosPreferenceRef = useRef<HTMLDivElement>(null);
  const accountPayableRef = useRef<HTMLDivElement>(null);

  // Matches the same pattern used in the other file
  const scrollToElement = (ref: React.RefObject<HTMLDivElement>) => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Reads RHF errors after trigger() and scrolls to the first broken section
  const scrollToFirstError = (errors: any) => {
    const hasError = (fields: string[]) =>
      fields.some((field) => {
        const parts = field.split(".");
        let obj: any = errors;
        for (const part of parts) {
          if (!obj?.[part]) return false;
          obj = obj[part];
        }
        return !!obj;
      });

    // Sections checked strictly top-to-bottom so we always land on the highest error
    if (hasError([
      "practiceName", "taxId", "npi", "ptan",
      "contactFirstName", "contactLastName",
      "contactEmail", "contactPhone",
      "practiceRequestInfo.dmeLicense",
      "practiceRequestInfo.billingCollagen",
      "practiceRequestInfo.billMedicare",
      "practiceRequestInfo.billMedicareAdvantage",
      "practiceRequestInfo.billCommercial",
      "practiceRequestInfo.interestedProductCategory",
    ])) {
      scrollToElement(practiceInfoRef);
    } else if (hasError([
      "specialization",
      "practiceRequestInfo.woundCareFocus",
      "practiceRequestInfo.averageWoundsTreatedPerMonth",
    ])) {
      scrollToElement(specializationRef);
    } else if (hasError([
      "emrIntegrationProvider",
      "emrProviderPracticeId",
    ])) {
      scrollToElement(emrIntegrationRef);
    } else if (hasError(["dosPreference"])) {
      scrollToElement(dosPreferenceRef);
    } else if (hasError([
      "apFirstName", "apLastName",
      "apPhone", "apEmail",
    ])) {
      scrollToElement(accountPayableRef);
    }
  };

  const { navigate } = useNavigation();
  const dispatch = useAppDispatch();
  const { practiceEditInfo } = usePracticeEditSlice();
  const { control, handleSubmit, reset, getValues, setValue, watch, trigger, formState: { errors } } =
    useForm<PracticeInfo>();
  const { practiceProductsInfo } = usePracticeProductsSlice();
  const [isAlertShow, setIsAlertShow] = React.useState(false);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  /** CS-2075 — the full "Add Pharmacy Config" label overflows the card header on phones. */
  const isCompactWidth = useMediaQuery(theme.breakpoints.down("sm"));
  const addPharmacyLabel = isCompactWidth ? "Add" : "Add Pharmacy Config";
  const [showDmeLicense, setShowDmeLicense] = React.useState<boolean | null>(null);
  const [showBillingCollagen, setShowBillingCollagen] = React.useState<boolean | null>(null);
  const defaultValues = useCallback(() => {
    if (practiceEditInfo) {
      reset(practiceEditInfo);
    }
  }, [practiceEditInfo, reset]);
  const [optionPopupShow, setOptionPopupShow] = React.useState<{
    message: string;
    show: boolean;
  }>({ message: "", show: false });
  const { allCodeTablesValuesMap, allCodeTableValueFetchStatus } = useCodetablesSlice();
  const [eMRIntegrationProviderList, setEMRIntegrationProviderList] = useState<CodeTable[]>([]);
  const { userInfo } = useAuthSlice();
  const [practiceSpecializationList, setPracticeSpecializationList] = React.useState<CodeTable[]>([]);
  const [productCategories, setProductCategories] = React.useState<CodeTable[]>([]);
  const [woundCareOptions, setWoundCareOptions] = React.useState<CodeTable[]>([]);
  const [dosPreferenceConfirmShow, setDosPreferenceConfirmShow] = React.useState<{
    message: string;
    show: boolean;
  }>({ message: "", show: false });
  const [auditLogPopupShow, setAuditLogPopupShow] = React.useState<{
    auditLogData: AuditLogData[];
    show: boolean;
  }>({ auditLogData: [], show: false });
  const {
    tokens: mTokens,
    isDark: isDarkModal,
    primaryBtnSx,
    inputFieldSx,
  } = useModalStyles();
  const cardSx = {
    borderRadius: "16px",
    border: `1px solid ${mTokens.border}`,
    backgroundColor: mTokens.bg,
    backgroundImage: "none",
    overflow: "hidden" as const,
    boxShadow: isDarkModal ? "0 10px 20px rgba(0,0,0,0.1)" : "none",
  };
  const sectionBodySx = { px: { xs: 1.5, md: 2 }, py: 1.25 };
  const practiceFormWrapSx = {
    borderRadius: "18px",
    border: `1px solid ${mTokens.border}`,
    backgroundColor: "transparent",
    p: { xs: 1.25, md: 1.75 },
  };
  const FieldLabel = ({ label, required = false }: { label: string; required?: boolean }) => (
    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: mTokens.textSecondary, mb: 0.5, display: "flex", alignItems: "center", gap: 0.4 }}>
      {label}{required && <Box component="span" sx={{ color: mTokens.accent }}>*</Box>}
    </Typography>
  );
  const controlHeight = "42px";
  const controlHeightSx = {
    minHeight: `${controlHeight} !important`,
    height: `${controlHeight} !important`,
    maxHeight: `${controlHeight} !important`,
    boxSizing: "border-box !important",
    alignItems: "center",
  };
  const mergeControlSx = (base: object) => {
    const nested = (base as Record<string, unknown>)["& .MuiOutlinedInput-root, & .MuiInputBase-root"];
    return {
      ...(nested && typeof nested === "object" ? nested : {}),
      ...controlHeightSx,
    };
  };
  const dropdownFieldWrapSx = (hasError = false) => {
    const base = inputFieldSx(hasError);
    return {
      ...base,
      width: "100%",
      "& .MuiAutocomplete-root, & .MuiFormControl-root, & .MuiTextField-root": { width: "100%", maxWidth: "100%" },
      "& .MuiOutlinedInput-root, & .MuiInputBase-root": mergeControlSx(base),
      "& .MuiAutocomplete-root .MuiOutlinedInput-root": {
        padding: "0 38px 0 12px !important",
        flexWrap: "nowrap !important",
        overflow: "hidden !important",
        minHeight: "42px !important",
        height: "42px !important",
        maxHeight: "42px !important",
      },
      "& .MuiInputBase-input, & .MuiOutlinedInput-input, & .MuiAutocomplete-input": {
        paddingTop: "8px !important",
        paddingBottom: "8px !important",
        height: "24px !important",
        lineHeight: "24px !important",
        boxSizing: "border-box",
      },
      "& .MuiChip-root": {
        height: "24px !important",
        maxHeight: "24px !important",
      },
      "& .MuiAutocomplete-endAdornment": {
        top: "50% !important",
        transform: "translateY(-50%)",
        right: "8px !important",
      },
    };
  };
  const textFieldWrapSx = (hasError = false) => dropdownFieldWrapSx(hasError);
  const saveBtnSx = {
    ...primaryBtnSx,
    color: "#fff",
    minHeight: "40px",
    width: { xs: "100%", sm: 260 },
    py: 0.8,
    "& .MuiTypography-root": { color: "#fff", fontWeight: 700 },
    "& .MuiButton-startIcon, & .MuiSvgIcon-root": { color: "#fff" },
    "&.Mui-disabled": { opacity: 0.45, color: "#fff", "& .MuiTypography-root": { color: "#fff" } },
  };
  /* Compact teal-outline action button for a SectionHeader's action slot
     (mirrors sectionActionBtnSx in PracticeEditView.tsx). */
  const sectionActionBtnSx = (hex: string = mTokens.accent) => ({
    textTransform: "none" as const,
    fontWeight: 700,
    fontSize: { xs: "0.74rem", sm: "0.82rem" },
    borderRadius: "10px",
    height: 36,
    px: { xs: 1, sm: 1.6 },
    minWidth: 0,
    flexShrink: 0,
    maxWidth: "100%",
    color: hex,
    borderColor: hex,
    backgroundColor: "transparent",
    whiteSpace: "nowrap" as const,
    letterSpacing: "0.04em",
    // The shared Button wraps its label in a block-level Typography, so centre
    // both the button content and that label.
    display: "inline-flex" as const,
    alignItems: "center",
    justifyContent: "center",
    // SectionHeader top-aligns its action whenever a subtitle is present; this
    // keeps the button centred against a subtitle that wraps to two lines.
    alignSelf: "center",
    "&:hover": {
      backgroundColor: `${hex}14`,
      borderColor: hex,
      boxShadow: "none",
    },
    "& .MuiTypography-root": {
      color: `${hex} !important`,
      fontWeight: 700,
      textAlign: "center" as const,
      lineHeight: 1.2,
      whiteSpace: "nowrap" as const,
    },
    "& .MuiButton-startIcon": { ml: 0, mr: 0.7 },
    "& .MuiButton-startIcon, & .MuiSvgIcon-root": { color: hex },
  });
  const questionRowSx = {
    display: { xs: "block", md: "flex" },
    alignItems: { md: "center" },
    gap: 1,
    py: 0.25,
  };
  /* One billing question per row, stacked tightly so the group stays compact. */
  const questionStackSx = {
    display: "flex",
    flexDirection: "column" as const,
    gap: 0.25,
  };
  const questionLabelSx = {
    display: "inline-block",
    width: { xs: "100%", md: 260 },
    flexShrink: 0,
    fontSize: "0.8rem",
    fontWeight: 600,
    color: mTokens.textSecondary,
    mb: { xs: 0.5, md: 0 },
  };
  /* Radio groups render each option in its own spaced Grid item, which stretches a
     two-option group across the whole row. Collapse that back to a tight inline pair. */
  const radioGroupWrapSx = {
    display: "inline-flex",
    flexDirection: "column" as const,
    alignItems: "flex-start",
    "& .MuiRadioGroup-root .MuiGrid-container": {
      width: "auto",
      m: 0,
      columnGap: 1.5,
      rowGap: 0.25,
    },
    "& .MuiRadioGroup-root .MuiGrid-item": { p: 0, width: "auto", maxWidth: "none" },
    "& .MuiFormControlLabel-root": { m: 0 },
    "& .MuiRadio-root": { p: 0.5, mr: 0.5 },
    "& .MuiFormControlLabel-label": { fontSize: "0.82rem", color: mTokens.textPrimary },
    "& .MuiFormHelperText-root": { m: 0, mt: 0.25 },
  };

  /** Pharmacy list — manufacturers flagged as pharmacies. */
  const { pharmacyInfoList } = useCommonReducer();
  const pharmacies = useMemo(() => pharmacyInfoList ?? [], [pharmacyInfoList]);
  const pharmacyOptions = useMemo(
    () =>
      pharmacies.map((pharmacy) => ({
        label: pharmacy.manufacturerName,
        value: pharmacy.manufacturerId,
      })),
    [pharmacies]
  );

  /**
   * Pharmacy billing codes the practice is configured with. Held in local state and
   * edited through a popup; handleSavePractice rebuilds practicePharmacyConfig from
   * this list, so it still persists with the single practice save (no pharmacy API).
   */
  const [pharmacyConfigRows, setPharmacyConfigRows] = useState<PracticePharmacyRow[]>([]);
  const [showPharmacyPopup, setShowPharmacyPopup] = useState(false);
  const [pharmacyEditRow, setPharmacyEditRow] = useState<PracticePharmacyRow | undefined>();
  const [pharmacyDeleteRow, setPharmacyDeleteRow] = useState<PracticePharmacyRow | null>(null);

  /** Pharmacies not yet configured — plus the one being edited, so it stays visible. */
  const availablePharmacyOptions = useMemo(() => {
    const usedIds = new Set(
      pharmacyConfigRows
        .filter((row) => row.manufacturerId !== pharmacyEditRow?.manufacturerId)
        .map((row) => Number(row.manufacturerId))
    );
    return pharmacyOptions.filter((option) => !usedIds.has(Number(option.value)));
  }, [pharmacyOptions, pharmacyConfigRows, pharmacyEditRow]);

  /** Add is offered only while at least one pharmacy is still unconfigured. */
  const canAddPharmacyRow =
    pharmacyOptions.length > 0 && pharmacyConfigRows.length < pharmacyOptions.length;

  const openAddPharmacyPopup = () => {
    setPharmacyEditRow(undefined);
    setShowPharmacyPopup(true);
  };

  const openEditPharmacyPopup = (row: PracticePharmacyRow) => {
    setPharmacyEditRow(row);
    setShowPharmacyPopup(true);
  };

  /**
   * Only hides the dialog — the edited row is deliberately left in place. Clearing it
   * here would flip the popup back to "Add" mode while it is still animating out,
   * which reads as a flicker. openAddPharmacyPopup clears it before an add.
   */
  const closePharmacyPopup = () => {
    setShowPharmacyPopup(false);
  };

  /**
   * CS-2075 — pharmacy billing codes save on their own endpoint, not with the
   * practice. Add / update / delete all post here; delete clears the code.
   */
  const persistPharmacyConfig = async (
    row: PracticePharmacyRow,
    billingCode: string,
    successMessage: string
  ) => {
    const pharmacyKey = toPharmacyConfigKey(row.manufacturerName);
    const practiceId = Number(getValues("practiceId") ?? practiceEditInfo?.practiceId);
    if (!pharmacyKey || !practiceId) return;

    try {
      const response: any = await dispatch(
        savePracticePharmacyConfigData({ practiceId, pharmacyKey, billingCode })
      );
      if (response?.payload && response.payload.message === "SUCCESS") {
        dispatch(setSingleAlertObj({ message: successMessage, alertType: "success" }));
        dispatch(fetchPracticeByIdData(String(practiceId)));
      } else {
        dispatch(
          setSingleAlertObj({
            message: response?.payload?.message || "Failed to save the pharmacy billing code.",
            alertType: "error",
          })
        );
      }
    } catch (error) {
      dispatch(
        setSingleAlertObj({
          message: "Error saving the pharmacy billing code.",
          alertType: "error",
        })
      );
    }
  };

  const savePharmacyRow = (row: PracticePharmacyRow) => {
    void persistPharmacyConfig(
      row,
      row.billingCode,
      `Billing code saved for ${row.manufacturerName}.`
    );
  };

  const confirmDeletePharmacyRow = () => {
    const row = pharmacyDeleteRow;
    setPharmacyDeleteRow(null);
    if (!row) return;
    void persistPharmacyConfig(row, "", `Billing code removed for ${row.manufacturerName}.`);
  };

  const pharmacyColumns = [
    { id: "manufacturerName", label: "Pharmacy", minWidth: 160 },
    { id: "billingCode", label: "Billing Code", minWidth: 140 },
    { id: "action", label: "Actions", minWidth: 90 },
  ];

  const pharmacyTableRows = useMemo(
    () =>
      pharmacyConfigRows.map((row) => ({
        ...row,
        manufacturerName: row.manufacturerName || "---",
        billingCode: row.billingCode || "---",
      })),
    [pharmacyConfigRows]
  );

  const pharmacyActionComponent = (row: any): JSX.Element => (
    <Box sx={{ display: "flex", gap: 1 }}>
      <CustomIconButton
        color="info"
        size="small"
        variant="outlined"
        tooltipMessage="Edit"
        tooltipPlacement="top"
        onClick={() => openEditPharmacyPopup(row as PracticePharmacyRow)}
      >
        <ModeOutlined fontSize="small" />
      </CustomIconButton>
      <CustomIconButton
        color="error"
        size="small"
        variant="outlined"
        tooltipMessage="Delete"
        tooltipPlacement="top"
        onClick={() => setPharmacyDeleteRow(row as PracticePharmacyRow)}
      >
        <DeleteOutlineOutlined fontSize="small" />
      </CustomIconButton>
    </Box>
  );

  const SwitchTextTrack = styled(Switch)({
    width: 74,
    height: 42,
    padding: 8,
    [`& .${switchClasses.switchBase}`]: {
      padding: 11,
      color: "#ff6a00",
    },
    [`& .${switchClasses.thumb}`]: {
      width: 20,
      height: 20,
      backgroundColor: "#fff",
    },
    [`& .${switchClasses.track}`]: {
      background:
        "linear-gradient(to right,rgb(161, 161, 161),rgb(84, 84, 84))",
      opacity: "1 !important",
      borderRadius: 20,
      position: "relative",
      "&:before, &:after": {
        display: "inline-block",
        position: "absolute",
        top: "50%",
        width: "50%",
        transform: "translateY(-50%)",
        color: "#fff",
        textAlign: "center",
        fontSize: "0.75rem",
        fontWeight: 500,
      },
      "&:before": {
        content: '"ON"',
        left: 4,
        opacity: 0,
      },
      "&:after": {
        content: '"OFF"',
        right: 4,
      },
    },
    [`& .${switchClasses.checked}`]: {
      [`&.${switchClasses.switchBase}`]: {
        color: "#185a9d",
        transform: "translateX(32px)",
        "&:hover": {
          backgroundColor: "rgba(24,90,257,0.08)",
        },
      },
      [`& .${switchClasses.thumb}`]: {
        backgroundColor: "#fff",
      },
      [`& + .${switchClasses.track}`]: {
        background: "linear-gradient(to right,rgb(0, 196, 88),rgb(0, 97, 29))",
        "&:before": {
          opacity: 1,
        },
        "&:after": {
          opacity: 0,
        },
      },
    },
  });

  const preferenceOptions = [
      { 
          value: "dateOfShipment", 
          label: "Date of Shipment"
      },
      { 
          value: "dateOfDelivery", 
          label:  "Date of Delivery"
      }
  ];

  // Pharmacy list for the Pharmacy Configuration section.
  useEffect(() => {
    dispatch(fetchPharmaciesList({ skipLoader: true }));
  }, [dispatch]);

  useEffect(() => {
    if (allCodeTableValueFetchStatus === "success" && allCodeTablesValuesMap) {
      const sortedEMRIntegrationProviderList = [
        ...getCodeTableValuesByTableName(allCodeTablesValuesMap, "EMRIntegrationProvider"),
      ].sort((a, b) => a.displayValue.localeCompare(b.displayValue));
      setEMRIntegrationProviderList(sortedEMRIntegrationProviderList);
      //CS-1242 - Start
      setPracticeSpecializationList(
        getCodeTableValuesByTableName(allCodeTablesValuesMap, "PracticeSpecializations")
      );
      setWoundCareOptions(
        getCodeTableValuesByTableName(allCodeTablesValuesMap, "WoundCare")
      );
      //CS-1242 - End
	// CS-1289 START
      const sortedProductCategoryList = [
        ...getCodeTableValuesByTableName(allCodeTablesValuesMap, PRODUCT_CATEGORY_CODETABLE),
      ].sort((a, b) => a.displayValue.localeCompare(b.displayValue));
      setProductCategories(sortedProductCategoryList);
	// CS-1289 END
    }
  }, [allCodeTablesValuesMap, allCodeTableValueFetchStatus]);

  const EMRIntegrationProviderListOptions = useMemo(() => {
    return eMRIntegrationProviderList.map(item => ({
      label: item.displayValue,
      value: item.keyName
    }));
  }, [eMRIntegrationProviderList]);
  //CS-1243 - end

  useEffect(() => {
    defaultValues();
  }, [defaultValues]);

  // CS-955
  useEffect(() => {
    setValue("practiceRequestInfo.dmeLicense", String(practiceEditInfo?.practiceRequestInfo?.dmeLicense ?? null));
    setValue("practiceRequestInfo.billingCollagen", String(practiceEditInfo?.practiceRequestInfo?.billingCollagen ?? null));
    setValue("practiceRequestInfo.billMedicare", String(practiceEditInfo?.practiceRequestInfo?.billMedicare ?? null));
    setValue("practiceRequestInfo.billMedicareAdvantage", String(practiceEditInfo?.practiceRequestInfo?.billMedicareAdvantage ?? null));
    setValue("practiceRequestInfo.billCommercial", String(practiceEditInfo?.practiceRequestInfo?.billCommercial ?? null));
  }, [practiceEditInfo]);

  // Seed one row per pharmacy that already has a billing code. Runs again after a save,
  // since getPracticeById returns the stored practicePharmacyConfig.
  useEffect(() => {
    if (!practiceEditInfo || pharmacies.length === 0) return;

    const savedConfig = practiceEditInfo.practicePharmacyConfig ?? {};
    const seededRows: PracticePharmacyRow[] = [];

    pharmacies.forEach((pharmacy) => {
      const configKey = toPharmacyConfigKey(pharmacy.manufacturerName, pharmacy.isPharmacy);
      if (!configKey) return;
      const code = savedConfig?.[configKey]?.[PHARMACY_CONFIG_BILLING_CODE] ?? "";
      if (String(code).trim() === "") return;

      seededRows.push({
        manufacturerId: pharmacy.manufacturerId,
        manufacturerName: pharmacy.manufacturerName,
        billingCode: String(code),
      });
    });

    setPharmacyConfigRows(seededRows);
  }, [practiceEditInfo, pharmacies]);

  // CS-1728 - Convert display values to key names for product categories
  useEffect(() => {
    if (practiceEditInfo?.practiceRequestInfo?.interestedProductCategory && productCategories.length > 0) {
      const displayValues = practiceEditInfo.practiceRequestInfo.interestedProductCategory;
      const keyNames = displayValues.map(mapDisplayValueToKeyName);
      setValue("practiceRequestInfo.interestedProductCategory", keyNames);
    }
  }, [practiceEditInfo, productCategories]);

  const dmeLicense = watch("practiceRequestInfo.dmeLicense");
  const billingCollagen = watch("practiceRequestInfo.billingCollagen");

  useEffect(() => {
    setShowDmeLicense(dmeLicense === "true" ? true : dmeLicense === "false" ? false : null);
    setShowBillingCollagen(billingCollagen === "true" ? true : billingCollagen === "false" ? false : null);
  }, [dmeLicense, billingCollagen]);

  //CS-1297 - Handled values wound care focus to Not available and averagewound treated per month based on specialization woundcare only if both values are not fille.
  useEffect(()=>{
    if(getValues("specialization") === PRACTICE_TYPE.WOUND_CARE && (getValues("practiceRequestInfo.woundCareFocus") === null || getValues("practiceRequestInfo.woundCareFocus") === "")){
      setValue("practiceRequestInfo.woundCareFocus", "Not Available");
    }
    if (getValues("specialization") === PRACTICE_TYPE.WOUND_CARE && (getValues("practiceRequestInfo.averageWoundsTreatedPerMonth") === null || getValues("practiceRequestInfo.averageWoundsTreatedPerMonth") === "")) {
      setValue("practiceRequestInfo.averageWoundsTreatedPerMonth", "0");
    }
  }, [watch("specialization"), watch("practiceRequestInfo.woundCareFocus"), watch("practiceRequestInfo.averageWoundsTreatedPerMonth"), PRACTICE_TYPE.WOUND_CARE]);

  // confirmOptionPopup now re-validates and scrolls on failure instead of blindly submitting
  const confirmOptionPopup = async () => {
    const isValid = await trigger();
    if (!isValid) {
      setOptionPopupShow({ message: "", show: false });
      scrollToFirstError(errors);
      return;
    }
    handleSubmit(handleSavePractice)();
    setOptionPopupShow({ message: "", show: false });
  };

  const cancelOptionPopup = () => {
    setOptionPopupShow({ message: "", show: false });
  };

  const confirmDosPreferencePopup = () => {
    setDosPreferenceConfirmShow({ message: "", show: false });
  };

  const cancelDosPreferencePopup = () => {
    setDosPreferenceConfirmShow({ message: "", show: false });
    setValue("dosPreference", practiceEditInfo?.dosPreference ?? null);
  };

  // CS-955
  const handleFieldChange = (field: any, value: any) => {
    setValue(field, value);
    if (field === "dmeLicense") {
      setShowDmeLicense(value === "true");
      if (value !== "true") {
        setValue("practiceRequestInfo.billingCollagen", null);
        setValue("practiceRequestInfo.billMedicare", null);
        setValue("practiceRequestInfo.billMedicareAdvantage", null);
        setValue("practiceRequestInfo.billCommercial", null);
        setShowBillingCollagen(null);
        // setShowBillMedicare(null);
        // setShowBillMedicareAdvantage(null);
        // setShowBillCommercial(null);
      }
    } else if (field === "billingCollagen") {
      setShowBillingCollagen(value=== "true");
      if (value !== "true") {
        setValue("practiceRequestInfo.billMedicare", null);
        setValue("practiceRequestInfo.billMedicareAdvantage", null);
        setValue("practiceRequestInfo.billCommercial", null);
        // setShowBillMedicare(null);
        // setShowBillMedicareAdvantage(null);
        // setShowBillCommercial(null);
      }
    }
  };
  //
  // CS-1289 START
  const productCategoryOptions = useMemo(() => {
      return productCategories.map(item => ({
          label: item.displayValue,
          value: item.keyName
      }));
  }, [productCategories]);

  const mapDisplayValueToKeyName = (displayValue: string): string => {
    const category = productCategories.find(item => item.displayValue === displayValue);
    return category?.keyName || displayValue;
  };
  // CS-1289 END

  const handleDosPreferenceOnChange = (e: any) => {
    if (practiceEditInfo?.dosPreference !== e.target.value) {
      setDosPreferenceConfirmShow({
        message: "This change will only affect new orders created from now on. Existing orders will NOT be updated. Do you really want to continue ?",
        show: true,
      });
    }
  };

  const handleConfirm = (e: any) => {
    console.log(e);
    setValue("dosChangeReason", e.reason);
  };

  const handleSavePractice = async (practiceInfoData: any) => {
    try {
      // CS-2075 — pharmacy billing codes have their own endpoint now, so they are
      // never part of the practice save payload.
      const practicePayload = { ...(practiceInfoData ?? {}) };
      delete practicePayload.practicePharmacyConfig;

      const response = await dispatch(editPracticeData(practicePayload));
      console.log(response);
      if (response.payload && response.payload.message === "SUCCESS") {
        let successObj: SingleAlertInfo = {
          message: "Practice Successfully Updated.",
          alertType: "success",
        };
        dispatch(setSingleAlertObj(successObj));
        contentChangedStatus();
        dispatch(fetchPracticeByIdData(practiceInfoData.practiceId));
      } else {
        let errorSentence = "";
        if (response.payload.errors.length <= 0) {
          errorSentence =
            response.payload.message || "An unknown error occurred.";
        } else {
          response.payload.errors.forEach((error: any, index: any) => {
            if (error.field) {
              errorSentence += `${error.field}: ${error.errorMessage}\n`;
            } else {
              errorSentence += `${error.reason}\n`;
            }
          });
        }
        let errorObj: SingleAlertInfo = {
          message: errorSentence,
          alertType: "error",
        };
        dispatch(setSingleAlertObj(errorObj));
      }
    } catch (error) {
      let errorObj: SingleAlertInfo = {
        message: "Error submitting practice data",
        alertType: "error",
      };
      dispatch(setSingleAlertObj(errorObj));
    }
  };

  // const handleBillerNotApplicableChange = (event: any) => {
  //   setIsBillerNotApplicable(event.target.checked);
  // };

  useEffect(() => {
    const claimSupported = watch('isClaimSupported') ?? false;
      if(!claimSupported){
        setValue('claimDueDays', null)
      }
  }, [watch('isClaimSupported')]);

  const onAuditHistoryClick = async () => {
    if (practiceEditInfo?.practiceId) {
      const response = await dispatch(fetchAuditLogsForPracticeDOSPreference(parseInt(practiceEditInfo.practiceId)));
      if (response.payload && response.payload.message === "SUCCESS") {
        setAuditLogPopupShow({ auditLogData: response.payload.data, show: true });
      }
    }
  };

  const handleProductCategoryChange = () => {
    const selected = getValues("practiceRequestInfo.interestedProductCategory");
    // CS-1728 - Behavioral logic for DOS preference
    if (!(selected || []).includes(PRODUCT_CATEGORY.CONSERVATIVE)) {
      setValue("dosPreference", "");
    }
  };

  const stepContent: any = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      <Box ref={practiceInfoRef} sx={cardSx}>
        <SectionHeader
          icon={<BusinessOutlined fontSize="small" />}
          title="Practice Information"
          subtitle="Enter Practice information."
        />
        <Box sx={sectionBodySx}>
      <Grid container spacing={1.5}>
        <Grid item md={4} xs={12}>
          <FieldLabel label="Practice Name" required />
          <Box sx={textFieldWrapSx(!!errors?.practiceName)}>
          <CustomTextField
            control={control}
            controllerName="practiceName"
            label="Practice Name"
            size="small"
            rules={{ required: "Practice name is required." }}
          />
          </Box>
        </Grid>
        <Grid item md={4} xs={12}>
          <FieldLabel label="Practice Tax ID" required />
          <Box sx={textFieldWrapSx(!!errors?.taxId)}>
          <CustomTextField
            control={control}
            controllerName="taxId"
            label="Practice Tax ID"
            size="small"
            rules={{
              required: "Practice Tax ID is required.",
              pattern: {
                value: /^((?!\s).)*$/,
                message: "Tax ID cannot contain spaces.",
              },
            }}
          />
          </Box>
        </Grid>
        <Grid item md={4}></Grid>
        <Grid item md={4} xs={12}>
          <FieldLabel label="Practice NPI" required />
          <Box sx={textFieldWrapSx(!!errors?.npi)}>
          <CustomTextField
            control={control}
            controllerName="npi"
            label="Practice NPI"
            size="small"
            rules={{
              required: "Practice NPI is required.",
              validate: {
                doesNotContainSpaces: (value: string) =>
                  !/\s/.test(value) || "NPI cannot contain spaces.",
                isAlphanumeric: (value: string) =>
                  /^[a-zA-Z0-9]+$/.test(value) ||
                  "NPI cannot contain special characters.",
              },
            }}
            maxLength={10}
          />
          </Box>
        </Grid>
        <Grid item md={4} xs={12}>
          <FieldLabel label="Practice PTAN" required />
          <Box sx={textFieldWrapSx(!!errors?.ptan)}>
          <CustomTextField
            control={control}
            controllerName="ptan"
            label="Practice PTAN"
            size="small"
            rules={{
              required: "Practice PTAN is required.",
              validate: {
                doesNotContainSpaces: (value: string) =>
                  !/\s/.test(value) || "PTAN cannot contain spaces.",
                isAlphanumeric: (value: string) =>
                  /^[a-zA-Z0-9]+$/.test(value) ||
                  "PTAN cannot contain special characters.",
              },
            }}
            maxLength={10}
          />
          </Box>
        </Grid>
        <Grid item md={12} xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FieldLabel label="Contact First Name" required />
            </Grid>
            <Grid item xs={12} md={4}>
              <FieldLabel label="Contact Middle Name" />
            </Grid>
            <Grid item xs={12} md={4}>
              <FieldLabel label="Contact Last Name" required />
            </Grid>
          </Grid>
          <Box sx={textFieldWrapSx(!!(errors as any)?.contactFirstName || !!(errors as any)?.contactLastName)}>
          <CustomNametField
            control={control}
            context="Contact"
            xs={12}
            md={4}
            isRequired={true}
            usePlaceholder={true}
          />
          </Box>
        </Grid>
        <Grid item md={4} xs={12}>
          <FieldLabel label="Contact Email" required />
          <Box sx={textFieldWrapSx(!!errors?.contactEmail)}>
          <CustomTextField
            control={control}
            controllerName="contactEmail"
            label="Contact Email"
            size="small"
            rules={{
              required: "Contact Email is required.",
              pattern: {
                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i,
                message: "Invalid email.",
              },
            }}
          />
          </Box>
        </Grid>
        <Grid item md={4} xs={12}>
          <FieldLabel label="Contact Phone" required />
          <Box sx={textFieldWrapSx(!!errors?.contactPhone)}>
          <CustomPatternTextField
            control={control}
            label="Contact Phone"
            name="contactPhone"
            size="small"
            rules={{
              required: "Contact Phone is required.",
              pattern: {
                value: /^(\+\d{1,2}\s)?\(\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/,
                message: "Invalid phone number.",
              },
            }}
          />
          </Box>
        </Grid>
        <Grid item md={4} xs={12}>
          <FieldLabel label="Contact Fax" />
          <Box sx={textFieldWrapSx()}>
          <CustomPatternTextField
            control={control}
            label="Contact Fax"
            name="contactFax"
            size="small"
            rules={{
              pattern: {
                value: /^(\+\d{1,2}\s)?\(\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/,
                message: "Invalid fax number.",
              },
            }}
          />
          </Box>
        </Grid>
        {/* CS-458 */}
        {/* One question per full-width row, stacked tightly, so the billing
            questions read as a single compact list instead of a 2-column grid. */}
        <Grid item xs={12}>
          <Box sx={questionStackSx}>
            <FormControl sx={{ width: "100%" }}>
              <Box sx={questionRowSx}>
                <Box component="label" sx={questionLabelSx}>Billing Type</Box>
                <Box sx={radioGroupWrapSx}>
                  <CustomRadioButton control={control} controllerName="billingType"
                    options={[
                      { value: "INDIVIDUAL", label: "Individual NPI" }, { value: "GROUP", label: "Group NPI" },
                    ]}
                    isDisabled={true}
                  />
                </Box>
              </Box>
            </FormControl>

            {showDmeLicense !== null && (
              <Box sx={questionRowSx}>
                <Box component="label" sx={questionLabelSx}>Do you have a DME license? <Box component="span" sx={{ color: mTokens.accent }}>*</Box></Box>
                <Box sx={radioGroupWrapSx}>
                  <CustomRadioButton
                    control={control}
                    controllerName="practiceRequestInfo.dmeLicense"
                    options={[
                      { value: true , label: "Yes" },
                      { value: false, label: "No" },
                    ]}
                    rules={{ required: "Please select an option for DME license." }}
                    onChange={(e) => handleFieldChange("dmeLicense", e.target.value)}
                  />
                </Box>
              </Box>
            )}

            {showDmeLicense && (
              <>
                <Box sx={questionRowSx}>
                  <Box component="label" sx={questionLabelSx}>
                    Will you be billing {getCategoryDisplayLabel(allCodeTablesValuesMap, PRODUCT_CATEGORY.CONSERVATIVE)}? <Box component="span" sx={{ color: mTokens.accent }}>*</Box></Box>
                  <Box sx={radioGroupWrapSx}>
                    <CustomRadioButton
                      control={control}
                      controllerName="practiceRequestInfo.billingCollagen"
                      options={[
                        { value: true, label: "Yes" },
                        { value: false, label: "No" },
                      ]}
                      rules={{ required: `Please select an option for billing ${getCategoryDisplayLabel(allCodeTablesValuesMap, PRODUCT_CATEGORY.CONSERVATIVE)}.` }}
                      onChange={(e) => handleFieldChange("billingCollagen", e.target.value)}
                    />
                  </Box>
                </Box>

                {showBillingCollagen && (
                  <>
                    <Box sx={questionRowSx}>
                      <Box component="label" sx={questionLabelSx}>
                        Will you bill Medicare? <Box component="span" sx={{ color: mTokens.accent }}>*</Box></Box>
                      <Box sx={radioGroupWrapSx}>
                        <CustomRadioButton
                          control={control}
                          controllerName="practiceRequestInfo.billMedicare"
                          options={[
                            { value: true, label: "Yes" },
                            { value: false, label: "No" },
                          ]}
                          rules={{ required: "Please select an option for billing Medicare." }}
                        />
                      </Box>
                    </Box>

                    <Box sx={questionRowSx}>
                      <Box component="label" sx={questionLabelSx}>
                        Will you bill Medicare Advantage? <Box component="span" sx={{ color: mTokens.accent }}>*</Box></Box>
                      <Box sx={radioGroupWrapSx}>
                        <CustomRadioButton
                          control={control}
                          controllerName="practiceRequestInfo.billMedicareAdvantage"
                          options={[
                            { value: true, label: "Yes" },
                            { value: false, label: "No" },
                          ]}
                          rules={{
                            required: "Please select an option for billing Medicare Advantage.",
                          }}
                        />
                      </Box>
                    </Box>

                    <Box sx={questionRowSx}>
                      <Box component="label" sx={questionLabelSx}>
                        Will you bill Commercial? <Box component="span" sx={{ color: mTokens.accent }}>*</Box>
                      </Box>
                      <Box sx={radioGroupWrapSx}>
                        <CustomRadioButton control={control} controllerName="practiceRequestInfo.billCommercial"
                          options={[{ value: true, label: "Yes" }, { value: false, label: "No" }]}
                          rules={{ required: "Please select an option for billing Commercial." }} />
                      </Box>
                    </Box>
                  </>
                )}
              </>
            )}
          </Box>
        </Grid>
        </Grid>
        </Box>
      </Box>

        {userInfo.entityName === USER_ROLE_ENTITY_NAMES.ADMIN && (
        <Box ref={specializationRef} sx={cardSx}>
          <SectionHeader
            icon={<HealthAndSafetyOutlined fontSize="small" />}
            title="Specialization"
            subtitle="Define the type of Specialization (Wound Care Only, Surgical Only or Wound Care and Surgical)."
          />
          <Box sx={sectionBodySx}>
          <Grid container spacing={1.5}>
          <Grid item md={4} xs={12}>
          <FieldLabel label="Type of Specialization(s)" required />
          <Box sx={dropdownFieldWrapSx()}>
          <CustomDropdownField
            control={control}
            name={"specialization"}
            options={
              practiceSpecializationList
                ? practiceSpecializationList.map((option) => ({
                  value: option.keyName,
                  label: option.displayValue,
                }))
                : []
            }
            label="Type of Specialization(s)"
            rules={{ required: "Type of Specialization(s) is required." }}
            onChangeSelect={(e) => {
              if(e?.value==="Surgical Only"){
                setValue("practiceRequestInfo.woundCareFocus", null);
                setValue("practiceRequestInfo.averageWoundsTreatedPerMonth", "");
              }
            }}
            // disabled={disableAllFields}
            sortOptions={false}
          />
          </Box>
          </Grid>
          <Grid item md={8} xs={12}></Grid>
          {watch("specialization") !== undefined && watch("specialization") !== "" && watch("specialization") !== "Surgical Only" && (<>
            <Grid item md={4} xs={12}>
            <FieldLabel label="Wound Care Focus" required />
            <Box sx={dropdownFieldWrapSx()}>
            <CustomDropdownField
              control={control}
              name={`practiceRequestInfo.${"woundCareFocus"}`}
              options={
                woundCareOptions
                  ? woundCareOptions.map((option) => ({
                    value: option.keyName,
                    label: option.displayValue,
                  }))
                  : []
              }
              label="Wound Care Focus"
              rules={{ required: "Wound Care Focus is required." }}
              sortOptions={false}
            />
            </Box>
            </Grid>
            <Grid item md={4} xs={12}>
              <FieldLabel label="Average Wounds Treated Per Month" required={watch("practiceRequestInfo.woundCareFocus") !== "Not Available"} />
              <Box sx={textFieldWrapSx()}>
              <CustomTextField control={control}
                controllerName={watch("practiceRequestInfo.woundCareFocus") === "Not Available" ? "" : `practiceRequestInfo.${"averageWoundsTreatedPerMonth"}`}
                label="Average Wounds Treated Per Month"
                size="small"
                isDisabled={watch("practiceRequestInfo.woundCareFocus") === "Not Available"}
                isEmpty={watch("practiceRequestInfo.woundCareFocus") === "Not Available"}
                rules={watch("practiceRequestInfo.woundCareFocus") === "Not Available" ? {} : { required: "Average Wounds Treated Per Month is required.", pattern: { value: /^[0-9]+$/, message: "Please enter a valid number." } }} />
              </Box>
            </Grid>
            <Grid item md={4}></Grid>
          </>)}
          { userInfo.entityName === USER_ROLE_ENTITY_NAMES.ADMIN && (
          <Grid item md={2} xs={12}>
          <Controller
            name="isClaimSupported"
            control={control}
              render={({ field }) => (
                <FormControlLabel
                control={
                  <SwitchTextTrack
                    {...field}
                    name={field.name}
                    checked={field.value}
                    size="medium"
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      field.onChange(isChecked);
                    }}
                  />
                }
                label="Claim Support"
                labelPlacement="start"
              />
            )}
          />
          </Grid>
        )}
        {watch("isClaimSupported") &&
          <Grid item md={4} xs={12}>
          <FieldLabel label="Add Claim Due Days" />
          <Box sx={textFieldWrapSx()}>
          <CustomNumberField control={control} controllerName={"claimDueDays"}
              label="Add Claim Due Days"
            />
          </Box>
          </Grid>
        }
          </Grid>
          </Box>
        </Box>
        )}
        <Box sx={cardSx}>
          <Box sx={sectionBodySx}>
        <Grid container spacing={1.5}>
        <Grid item md={12}>
              <Box sx={questionRowSx}>
              <Typography sx={{ ...questionLabelSx, width: "auto", mr: 2 }}>Product Category</Typography>
              <CustomCheckboxGroup
                control={control}
                controllerName="practiceRequestInfo.interestedProductCategory"
                options={productCategoryOptions}
                rules={{ required: "At least one product category must be selected." }}
                onChange={handleProductCategoryChange} />
              </Box>
        </Grid>
        </Grid>
          </Box>
        </Box>
      {/* CS-1242 - start */}
      {(userInfo.entityName === USER_ROLE_ENTITY_NAMES.ADMIN || userInfo.userRole === USER_ROLES.ROLE_PRACTICEADMIN) && (
        <Box sx={cardSx}>
          <SectionHeader
            icon={<VerifiedUserOutlined fontSize="small" />}
            title="Auto-Eligibility"
            subtitle="Check the button to activate Auto - Eligibility Creation Service."
          />
          <Box sx={sectionBodySx}>
            <Controller
              name="isAutoIVREnabled"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <SwitchTextTrack
                      {...field}
                      name={field.name}
                      checked={field.value}
                      size="medium"
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        field.onChange(isChecked);
                      }}
                    />
                  }
                  // disabled={editForm}
                  // CS-1342 - Start
                  label="Auto-Eligibility-Service (pVerify)" //CS-1369 - Label changes
                  // CS-1342 - End
                  labelPlacement="start"
                />
              )}
            />
          </Box>
        </Box>
      )}
      {/* CS-1242 - end */}
      {/* CS-1243 - start */}
      {(userInfo.entityName === USER_ROLE_ENTITY_NAMES.ADMIN || userInfo.userRole === USER_ROLES.ROLE_PRACTICEADMIN) && (
        <Box ref={emrIntegrationRef} sx={cardSx}>
          <SectionHeader
            icon={<SyncAltOutlined fontSize="small" />}
            title="EMR Integration"
            subtitle="Check the button to activate EMR Integration Service."
          />
          <Box sx={sectionBodySx}>
            <Controller
              name="isEMREnabled"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <SwitchTextTrack
                      {...field}
                      name={field.name}
                      checked={field.value}
                      size="medium"
                      // disabled={!(canEditProduct || !editForm)} 
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        field.onChange(isChecked);

                        // Reset EMR-related fields when toggle is turned off
                        if (!isChecked) {
                          setValue("emrIntegrationProvider", "");
                          setValue("emrProviderPracticeId", "");
                        }
                      }}
                    />
                  }
                  // disabled={editForm}
                  label="EMR Integration Service"
                  labelPlacement="start"
                />
              )}
            />
          {watch("isEMREnabled") && (
            <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
              <Grid item md={4} xs={12}>
              <FieldLabel label="EMR Integration Provider" required />
              <Box sx={dropdownFieldWrapSx()}>
              <CustomDropdownField
                name="emrIntegrationProvider"
                control={control}
                options={EMRIntegrationProviderListOptions}
                label="EMR Integration Provider"
                rules={{
                  required: "EMR integration provider is required.",
                  validate: (value: string) => {
                    const validOptions = EMRIntegrationProviderListOptions.map(option => option.value);
                    if (value && !validOptions.includes(value)) {
                      return "Please select a valid EMR Integration Provider from the list.";
                    }
                    return true;
                  }
                }}
              />
              </Box>
              </Grid>
              <Grid item md={4} xs={12}>
              <FieldLabel label="EMR Provider Practice ID" required />
              <Box sx={textFieldWrapSx()}>
              <CustomTextField
                control={control}
                controllerName="emrProviderPracticeId"
                label="EMR Provider Practice ID"
                size="small"
                rules={{
                  required: "EMR Provider Practice ID is required.",
                  validate: (value: string) => {
                    const specialCharPattern = /[^a-zA-Z0-9\-_]/;
                    if (value && specialCharPattern.test(value)) {
                      return "EMR Provider Practice ID should not contain special characters.";
                    }
                    return true;
                  }
                }}
              />
              </Box>
              </Grid>
            </Grid>
          )}
          </Box>
        </Box>
      )}
      {/* CS-1243 - end */}
      {/* CS-1997 - Free Shipping (P&B only) practice-level toggle */}
      {userInfo.entityName === USER_ROLE_ENTITY_NAMES.ADMIN && (
        <Box sx={cardSx}>
          <SectionHeader
            icon={<LocalShippingOutlined fontSize="small" />}
            title="Free Shipping (P&B only)"
            subtitle="Check the button to allow free shipping for this practice."
          />
          <Box sx={sectionBodySx}>
            <Controller
              name="allowFreeShipping"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <SwitchTextTrack
                      {...field}
                      name={field.name}
                      checked={field.value || false}
                      size="medium"
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                      }}
                    />
                  }
                  label="Enable Free Shipping"
                  labelPlacement="start"
                />
              )}
            />
          </Box>
        </Box>
      )}

      {(watch("practiceRequestInfo.interestedProductCategory") || []).includes(PRODUCT_CATEGORY.CONSERVATIVE) && (
        <Box ref={dosPreferenceRef} sx={cardSx}>
          <SectionHeader
            icon={<AssignmentOutlined fontSize="small" />}
            title="Date of Service Preference"
            subtitle="Option to select “Date of Service” based on either “Date of Shipment” or “Date of Delivery”."
            action={
              (userInfo.entityName === USER_ROLE_ENTITY_NAMES.ADMIN || userInfo.userRole === USER_ROLES.ROLE_PRACTICEADMIN) ? (
                  <Button color="info" variant="outlined" removeUpperCase onClick={onAuditHistoryClick} startIcon={<ManageSearch />} sx={{ textTransform: "none", fontWeight: 700, fontSize: "0.78rem", borderRadius: "8px", minHeight: "32px", py: 0.4, "& .MuiTypography-root": { fontWeight: 700 } }}>
                    Audit Trail
                  </Button>
              ) : undefined
            }
          />
          <Box sx={sectionBodySx}>
            <Box sx={radioGroupWrapSx}>
              <CustomRadioButton
                control={control}
                controllerName="dosPreference"
                options={preferenceOptions}
                isDisabled={!(userInfo.entityName === USER_ROLE_ENTITY_NAMES.ADMIN || userInfo.userRole === USER_ROLES.ROLE_PRACTICEADMIN)}
                onChange={handleDosPreferenceOnChange}
                rules={{
                  required: "Date of Service Preference is required.",
                }}
              />
            </Box>
          </Box>
        </Box>
      )}

      <Box ref={accountPayableRef} sx={cardSx}>
        <SectionHeader
          icon={<AccountBalanceWalletOutlined fontSize="small" />}
          title="Account Payable Information"
          subtitle="Enter Account Payable Information."
        />
        <Box sx={sectionBodySx}>
      <Grid container spacing={1.5}>
        <Grid item md={12} xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FieldLabel label="AP Contact First Name" required />
            </Grid>
            <Grid item xs={12} md={4}>
              <FieldLabel label="AP Contact Middle Name" />
            </Grid>
            <Grid item xs={12} md={4}>
              <FieldLabel label="AP Contact Last Name" required />
            </Grid>
          </Grid>
          <Box sx={textFieldWrapSx(!!(errors as any)?.apFirstName || !!(errors as any)?.apLastName)}>
          <CustomNametField
            control={control}
            context="ap"
            label="AP Contact"
            xs={12}
            md={4}
            isRequired={true}
            usePlaceholder={true}
          />
          </Box>
        </Grid>
        <Grid item md={4} xs={12}>
          <FieldLabel label="AP Contact Phone" required />
          <Box sx={textFieldWrapSx(!!errors?.apPhone)}>
          <CustomPatternTextField
            control={control}
            label="AP Contact Phone"
            name="apPhone"
            size="small"
            rules={{
              required: "AP Contact Phone is required.",
              pattern: {
                value: /^(\+\d{1,2}\s)?\(\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/,
                message: "Invalid phone number.",
              },
            }}
          />
          </Box>
        </Grid>
        <Grid item md={4} xs={12}>
          <FieldLabel label="AP Contact Email" required />
          <Box sx={textFieldWrapSx(!!errors?.apEmail)}>
          <CustomTextField
            control={control}
            controllerName="apEmail"
            label="AP Contact Email"
            size="small"
            rules={{
              required: "AP Contact Email is required.",
              pattern: {
                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i,
                message: "Invalid email.",
              },
            }}
          />
          </Box>
        </Grid>
      </Grid>
        </Box>
      </Box>

    </Box>
  );

  /**
   * CS-2075 — rendered AFTER the practice Save button: this section saves through its
   * own endpoint, so it must not look like it belongs to the Save above it.
   * Admin, Super Admin and Practice Admin all get the same add / edit / delete
   * rights here, so the gate is role membership only.
   */
  const pharmacyConfigSection =
    userInfo.entityName === USER_ROLE_ENTITY_NAMES.ADMIN ||
    userInfo.userRole === USER_ROLES.ROLE_PRACTICEADMIN ? (
      // mt: separates this card from the practice Save button above it — the two
      // save through different endpoints and shouldn't read as one block.
      <Box sx={{ ...cardSx, mt: 2.5 }}>
        <SectionHeader
          icon={<MedicalServicesOutlined fontSize="small" />}
          title="Pharmacy Configuration"
          subtitle="Add each pharmacy this practice works with and the billing code that pharmacy assigned to it. The code identifies the provider during prescription processing, and each pharmacy issues its own."
          action={
            canAddPharmacyRow ? (
              <Button
                startIcon={<AddCircleOutlineOutlined />}
                onClick={openAddPharmacyPopup}
                variant="outlined"
                removeUpperCase
                sx={sectionActionBtnSx()}
              >
                {pharmacyConfigRows.length === 0 ? addPharmacyLabel : "Add More"}
              </Button>
            ) : undefined
          }
        />
        <Box sx={sectionBodySx}>
          {pharmacyOptions.length === 0 ? (
            <Typography variant="body2" sx={{ color: mTokens.textSecondary }}>
              No pharmacies are configured yet. Turn on the Pharmacy toggle for a
              manufacturer to make it available here.
            </Typography>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table
                columns={pharmacyColumns}
                rows={pharmacyTableRows}
                disablePagination={true}
                emptyText="No pharmacy billing codes added yet. Use Add Pharmacy Config to configure one."
                actionComponent={(row: any) => pharmacyActionComponent(row)}
              />
            </Box>
          )}
        </Box>
      </Box>
    ) : null;

  return (
    <>
      <form>
        <CustomAlert
          open={isAlertShow}
          onClose={() => {
            setIsAlertShow(false);
          }}
          severity="info"
          message="Feature currently under development. Stay tuned for updates!"
        />
        <FormGroup>
          <Box sx={practiceFormWrapSx}>
            {stepContent}
            <Grid
              container
              justifyContent="flex-end"
              sx={{ mt: 1.5, flexWrap: "wrap" }}
              spacing={1}
            >
              <Grid item xs={12} sm="auto">
                <Button
                  sx={saveBtnSx}
                  removeUpperCase
                  onClick={async () => {
                    // Validate first; only open the confirm popup if the form is clean
                    const isValid = await trigger();
                    if (!isValid) {
                      scrollToFirstError(errors);
                      return;
                    }
                    setOptionPopupShow({ message: "Do you really want to update practice details? ", show: true });
                  }}
                  startIcon={<Save />}
                >
                  Save
                </Button>
              </Grid>
            </Grid>
          </Box>
          {pharmacyConfigSection}
        </FormGroup>
      </form>

      <OptionsPopup
        open={optionPopupShow.show}
        onClose={() => setOptionPopupShow({ message: "", show: false })}
        variant="confirm"
        message={<>{optionPopupShow.message}</>}
        buttons={[
          { name: "Confirm", color: "primary", onClick: confirmOptionPopup },
          { name: "Cancel", color: "secondary", onClick: cancelOptionPopup },
        ]}
      />

      {/* CS-2075 — pharmacy add / edit + delete confirmation */}
      <PracticePharmacyPopup
        open={showPharmacyPopup}
        onClose={closePharmacyPopup}
        pharmacyOptions={availablePharmacyOptions}
        onSave={savePharmacyRow}
        editData={pharmacyEditRow}
        isEditMode={!!pharmacyEditRow}
      />
      <OptionsPopup
        open={!!pharmacyDeleteRow}
        onClose={() => setPharmacyDeleteRow(null)}
        variant="warning"
        message={
          <>
            {`Are you sure you want to remove the billing code for ${pharmacyDeleteRow?.manufacturerName ?? "this pharmacy"}?`}
          </>
        }
        buttons={[
          { name: "Yes", color: "primary", onClick: confirmDeletePharmacyRow },
          { name: "No", color: "error", onClick: () => setPharmacyDeleteRow(null) },
        ]}
      />
      <DOSConfirmationPopup
        open={dosPreferenceConfirmShow.show}
        onClose={() => setDosPreferenceConfirmShow({ message: "", show: false })}
        message={dosPreferenceConfirmShow.message}
        onConfirm={handleConfirm}
        buttons={[
          { name: "Confirm", color: "warning", type: "submit", onClick: confirmDosPreferencePopup },
          { name: "Cancel", color: "error", onClick: cancelDosPreferencePopup },
        ]}
      />
      <AuditLogDisplayPopup
        open={auditLogPopupShow.show}
        onClose={() => setAuditLogPopupShow({ auditLogData: [], show: false })}
        auditLogData={auditLogPopupShow.auditLogData}
      />
    </>
  );
};

export default PracticeEditViewForm;
